import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Center,
  Tooltip,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type SubmitProgressBulkMutation,
  useSubmitProgressBulkMutation,
} from "@src/__generated__/graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { onError } from "@src/utils/apollo";
import { useStore } from "@src/utils/hooks";
import { PercentModel } from "@src/utils/models";
import { observer } from "mobx-react-lite";
import type { FinishProjectSummary } from "../models";
import BudgetItem from "./BudgetItem";

export interface OurWorkIncompletedProps {
  items: FinishProjectSummary["incomplete_our_work_budget_items"];
  onCompleted: (
    items: SubmitProgressBulkMutation["submitProgressBulk"],
  ) => void;
}

const OurWorkIncompleted = ({
  items,
  onCompleted,
}: OurWorkIncompletedProps) => {
  const appStore = useStore();

  const [submitProgress, { loading }] = useSubmitProgressBulkMutation({
    ...onError(),
    onCompleted(data) {
      onCompleted(data.submitProgressBulk);
    },
  });

  const onAction = () => {
    const ids = items.flatMap((i) =>
      i.lastWorkInProgressItemStat
        ? [i.lastWorkInProgressItemStat?.work_in_progress_id]
        : [],
    );

    if (ids.length) {
      submitProgress({
        variables: {
          ids,
          progress: PercentModel.fromValue(100).rawValue,
        },
      });
    } else {
      appStore.UIStore.toast({
        status: "error",
        title: t`Something went wrong`,
      });
    }
  };

  return (
    <Alert
      alignItems="top"
      rowGap="8"
      gridTemplateColumns="max-content 1fr"
      display="grid"
      status="error"
      variant="top-accent"
    >
      <AlertIcon />
      <div>
        <AlertTitle>
          <Trans>Project progress is not 100 %</Trans>
        </AlertTitle>
        <AlertDescription>
          <div>
            <div className="flex items-center mb-5">
              <p>
                <Trans>These budget items progress is lower than 100 %:</Trans>
              </p>
              <Tooltip
                label={
                  <div
                    className="text-center p-3 text-lg"
                    style={{ maxWidth: 200 }}
                  >
                    <p>
                      <Trans>
                        To close a project, you need to set all budget items
                        progress to 100 %.
                      </Trans>
                    </p>
                  </div>
                }
              >
                <div className="ml-3">
                  <Icon name="info-circle" color="#919EAB" variant="solid" />
                </div>
              </Tooltip>
            </div>
            <div>
              {items.map((bi) => (
                <BudgetItem key={bi.id} ourWorkItem={bi} />
              ))}
            </div>
          </div>
        </AlertDescription>
      </div>
      <Center gridColumn="span 2">
        <Button
          _hover={{ bg: "red.200" }}
          colorScheme="grey"
          isLoading={loading}
          onClick={() => onAction()}
          variant="outline"
        >
          <Trans>Set all budget item progresses to 100 %</Trans>
        </Button>
      </Center>
    </Alert>
  );
};

export default observer(OurWorkIncompleted);
